import { locationService } from '@grafana/runtime';
import { SceneObjectStateChangedEvent } from '../core/events.js';
import { writeSceneLog } from '../utils/writeSceneLog.js';
import { UniqueUrlKeyMapper } from './UniqueUrlKeyMapper.js';
import { syncStateFromUrl, isUrlValueEqual, getUrlState } from './utils.js';

class UrlSyncManager {
  constructor() {
    this._urlKeyMapper = new UniqueUrlKeyMapper();
    this._stateSub = null;
    this._locationSub = null;
    this._ignoreNextLocationUpdate = false;
    this._onLocationUpdate = (location) => {
      if (this._ignoreNextLocationUpdate) {
        this._ignoreNextLocationUpdate = false;
        return;
      }
      if (this._lastPath !== location.pathname) {
        return;
      }
      const urlParams = new URLSearchParams(location.search);
      this._urlKeyMapper.rebuildIndex(this._sceneRoot);
      syncStateFromUrl(this._sceneRoot, urlParams, this._urlKeyMapper);
      this._lastPath = location.pathname;
    };
    this._onStateChanged = ({ payload }) => {
      const changedObject = payload.changedObject;
      if (changedObject.urlSync) {
        const newUrlState = changedObject.urlSync.getUrlState();
        const searchParams = locationService.getSearch();
        const mappedUpdated = {};
        this._urlKeyMapper.rebuildIndex(this._sceneRoot);
        for (const [key, newUrlValue] of Object.entries(newUrlState)) {
          const uniqueKey = this._urlKeyMapper.getUniqueKey(key, changedObject);
          const currentUrlValue = searchParams.getAll(uniqueKey);
          if (!isUrlValueEqual(currentUrlValue, newUrlValue)) {
            mappedUpdated[uniqueKey] = newUrlValue;
          }
        }
        if (Object.keys(mappedUpdated).length > 0) {
          this._ignoreNextLocationUpdate = true;
          locationService.partial(mappedUpdated, true);
        }
      }
    };
  }
  initSync(root) {
    if (!this._locationSub) {
      writeSceneLog("UrlSyncManager", "New location listen");
      this._locationSub = locationService.getHistory().listen(this._onLocationUpdate);
    }
    if (this._stateSub) {
      writeSceneLog("UrlSyncManager", "Unregister previous scene state subscription", this._sceneRoot.state.key);
      this._stateSub.unsubscribe();
    }
    this._sceneRoot = root;
    this._lastPath = locationService.getLocation().pathname;
    this._stateSub = root.subscribeToEvent(SceneObjectStateChangedEvent, this._onStateChanged);
    this.syncFrom(this._sceneRoot);
  }
  cleanUp(root) {
    if (this._sceneRoot !== root) {
      return;
    }
    writeSceneLog("UrlSyncManager", "Clean up");
    if (this._locationSub) {
      this._locationSub();
      writeSceneLog("UrlSyncManager", "Unregister history listen");
      this._locationSub = null;
    }
    if (this._stateSub) {
      this._stateSub.unsubscribe();
      this._stateSub = null;
      writeSceneLog(
        "UrlSyncManager",
        "Root deactived, unsub to state",
        "same key",
        this._sceneRoot.state.key === root.state.key
      );
    }
  }
  syncFrom(sceneObj) {
    const urlParams = locationService.getSearch();
    this._urlKeyMapper.rebuildIndex(this._sceneRoot);
    syncStateFromUrl(sceneObj, urlParams, this._urlKeyMapper);
  }
  getUrlState(root) {
    return getUrlState(root);
  }
}
let urlSyncManager;
function getUrlSyncManager() {
  if (!urlSyncManager) {
    urlSyncManager = new UrlSyncManager();
  }
  return urlSyncManager;
}

export { UrlSyncManager, getUrlSyncManager };
//# sourceMappingURL=UrlSyncManager.js.map
